import { AutoCompleteOption } from "../components/form/APAutoComplete";
import {
  CriteriaLabelMap,
  ListQuote,
  PersonalisedQuoteVehicleInfo,
  QuoteComparisonItem,
  selectableQuoteComparisonCriteria,
  VehiclePurchaseType,
} from "../types/Quote";
import { VehicleOption, VehicleOptions } from "../types/Vehicles";

export type QuoteSortOption = "taxSavings" | "netWeeklyCost" | "lastUpdated";

const puchaseTypeLabelMap: Record<VehiclePurchaseType, string> = {
  new: "New",
  used: "Used",
  "sale-and-leaseback": "Lease my own car",
  refinance: "Refinance",
  renovation: "Renovation",
  "external-financier-takeover": "External Financier Takeover",
};

export const getPurchaseTypeLabel = (
  purchaseType: VehiclePurchaseType
): string => {
  return puchaseTypeLabelMap[purchaseType];
};

export const filterQuotes = <T extends ListQuote>(
  quotes: T[],
  isInternal: boolean,
  removeInvalid: boolean = false,
  purchaseTypes?: VehiclePurchaseType[]
) => {
  return quotes.filter(
    (quote) =>
      isInternal ||
      (quote.config.isVisible &&
        (!removeInvalid || quote.config.calculationStatus !== "invalid") &&
        (!purchaseTypes || purchaseTypes.includes(quote.vehicle.purchaseType)))
  );
};

export const sortQuotes = (
  quotes: ListQuote[],
  quoteSortOptionId: QuoteSortOption,
  primaryQuoteId: string | null
): ListQuote[] => {
  const quotesToSort = [...quotes];

  let compareFunction: (a: ListQuote, b: ListQuote) => number;

  switch (quoteSortOptionId) {
    case "netWeeklyCost":
      compareFunction = (a: ListQuote, b: ListQuote) => {
        if (
          a.salaryComparison.netCost.weekly < b.salaryComparison.netCost.weekly
        ) {
          return -1;
        }
        if (
          a.salaryComparison.netCost.weekly > b.salaryComparison.netCost.weekly
        ) {
          return 1;
        }
        return 0;
      };
      break;
    case "lastUpdated":
      compareFunction = (a: ListQuote, b: ListQuote) => {
        if (a.identifiers.crmQuoteId === primaryQuoteId) {
          return -1;
        } else if (b.identifiers.crmQuoteId === primaryQuoteId) {
          return 1;
        } else if (a.config.updatedAt > b.config.updatedAt) {
          return -1;
        }
        if (a.config.updatedAt < b.config.updatedAt) {
          return 1;
        }
        return 0;
      };
      break;
    case "taxSavings":
      compareFunction = (a: ListQuote, b: ListQuote) => {
        if (
          a.salaryComparison.netCost.weekly > b.salaryComparison.netCost.weekly
        ) {
          return -1;
        }
        if (
          a.salaryComparison.netCost.weekly < b.salaryComparison.netCost.weekly
        ) {
          return 1;
        }
        return 0;
      };
      break;
  }

  quotesToSort.sort(compareFunction);

  return quotesToSort;
};

export const buildQuoteComparisonQueryString = (
  quoteIds: string[],
  primaryCriteria: keyof QuoteComparisonItem,
  selectedCriteria: (keyof QuoteComparisonItem)[]
) => {
  const queryParams = new URLSearchParams();

  queryParams.set("quoteIds", quoteIds.join(","));

  queryParams.set("primaryCriteria", primaryCriteria);

  if (selectedCriteria.length > 0) {
    queryParams.set("selectedCriteria", selectedCriteria.join(","));
  }

  return queryParams.toString();
};

export const buildSelectableQuoteComparisonCriteriaOption = (
  criteria: keyof QuoteComparisonItem
): AutoCompleteOption => {
  return {
    id: criteria,
    label: CriteriaLabelMap[criteria],
  };
};

export const buildSelectableQuoteComparisonCriteriaOptions = (
  excludeCriteria?: keyof QuoteComparisonItem
): AutoCompleteOption[] => {
  return selectableQuoteComparisonCriteria.reduce((acc, criteria) => {
    if (excludeCriteria && criteria === excludeCriteria) {
      return acc;
    }
    acc.push(buildSelectableQuoteComparisonCriteriaOption(criteria));

    return acc;
  }, [] as AutoCompleteOption[]);
};

export type PersonaliseQuoteInputs = {
  options: Record<string, boolean>;
  inclusions: Record<string, boolean>;
};

export type QuotePersonalisationScope = "all" | "vehicle" | "manufacturer";

export const filterSelectedOptions = (
  options: VehicleOptions,
  quoteVehicleInfo: PersonalisedQuoteVehicleInfo
): VehicleOptions => {
  const selectedDealerOptions = Object.values(options.options.dealer).filter(
    (option) =>
      quoteVehicleInfo.catchEEquipmentIds.includes(option.catchEOptionId)
  );
  const selectedManufacturerOptions = Object.values(
    options.options.manufacturer
  ).filter((option) =>
    quoteVehicleInfo.catchEEquipmentIds.includes(option.catchEOptionId)
  );
  const selectedInclusions = Object.values(options.options.inclusion).filter(
    (option) =>
      Object.keys(quoteVehicleInfo.catchEInclusionSellPrices).includes(
        option.catchEOptionId
      )
  );

  return {
    ...options,
    options: {
      dealer: selectedDealerOptions.reduce((acc, option) => {
        acc[option.id] = option;
        return acc;
      }, {} as Record<string, VehicleOption>),
      manufacturer: selectedManufacturerOptions.reduce((acc, option) => {
        acc[option.id] = option;
        return acc;
      }, {} as Record<string, VehicleOption>),
      inclusion: selectedInclusions.reduce((acc, option) => {
        acc[option.id] = option;
        return acc;
      }, {} as Record<string, VehicleOption>),
    },
  };
};
